import {
  DesiredItemState,
  DesiredVariantState,
  MasterLabelDebugInfo,
  MasterLabelDiffSummary,
  MasterLabelStateSaveRequest,
  MasterUPCData,
} from '../types';

type CropSection = 'variant' | 'pending' | 'no-variant' | 'non-front' | 'carton' | 'inactive' | 'different-item';

interface CropPlacement {
  cropId: string;
  itemNumber: string;
  section: CropSection;
  variantId: string | null;
}

interface VariantSnapshot {
  id: string;
  persistedVariantId: string | null;
  itemNumber: string;
  representativeCropId: string | null;
  cropIds?: string[];
}

interface ItemDiffAccumulator {
  itemNumber: string;
  cropChangeCount: number;
  representativeChangeCount: number;
  draftVariantCount: number;
  dirtyCropIds: Set<string>;
  dirtyVariantIds: Set<string>;
}

function createAccumulator(itemNumber: string): ItemDiffAccumulator {
  return {
    itemNumber,
    cropChangeCount: 0,
    representativeChangeCount: 0,
    draftVariantCount: 0,
    dirtyCropIds: new Set(),
    dirtyVariantIds: new Set(),
  };
}

function ensureAccumulator(
  accByItem: Map<string, ItemDiffAccumulator>,
  itemNumber: string
): ItemDiffAccumulator {
  let acc = accByItem.get(itemNumber);
  if (!acc) {
    acc = createAccumulator(itemNumber);
    accByItem.set(itemNumber, acc);
  }
  return acc;
}

function sortIds(ids: string[]): string[] {
  return [...ids].sort((a, b) => a.localeCompare(b));
}

function normalizeVariantId(variantId?: string | null): string | null {
  return variantId ?? null;
}

function cropPlacementKey(placement: CropPlacement | undefined): string {
  if (!placement) return '__missing__';
  return [placement.itemNumber, placement.section, placement.variantId ?? ''].join('|');
}

function flattenCropPlacements(upcs: MasterUPCData[]): Map<string, CropPlacement> {
  const placements = new Map<string, CropPlacement>();

  for (const upc of upcs) {
    for (const variant of upc.variants) {
      for (const crop of variant.crops) {
        placements.set(crop.id, {
          cropId: crop.id,
          itemNumber: upc.itemNumber,
          section: 'variant',
          variantId: normalizeVariantId(variant.id),
        });
      }
    }
    for (const crop of upc.pendingCrops ?? []) {
      placements.set(crop.id, {
        cropId: crop.id,
        itemNumber: upc.itemNumber,
        section: 'pending',
        variantId: null,
      });
    }
    for (const crop of upc.unassignedCrops) {
      placements.set(crop.id, {
        cropId: crop.id,
        itemNumber: upc.itemNumber,
        section: 'no-variant',
        variantId: null,
      });
    }
    for (const crop of upc.nonFrontCrops) {
      placements.set(crop.id, {
        cropId: crop.id,
        itemNumber: upc.itemNumber,
        section: 'non-front',
        variantId: null,
      });
    }
    for (const crop of upc.cartonCrops) {
      placements.set(crop.id, {
        cropId: crop.id,
        itemNumber: upc.itemNumber,
        section: 'carton',
        variantId: null,
      });
    }
    for (const crop of upc.inactiveCrops ?? []) {
      placements.set(crop.id, {
        cropId: crop.id,
        itemNumber: upc.itemNumber,
        section: 'inactive',
        variantId: null,
      });
    }
    for (const cropId of upc.differentItemCropIds ?? []) {
      placements.set(cropId, {
        cropId,
        itemNumber: upc.itemNumber,
        section: 'different-item',
        variantId: null,
      });
    }
  }

  return placements;
}

function flattenPersistedVariants(upcs: MasterUPCData[]): Map<string, VariantSnapshot> {
  const variants = new Map<string, VariantSnapshot>();
  for (const upc of upcs) {
    for (const variant of upc.variants) {
      const persistedVariantId = variant.persistedVariantId ?? null;
      if (!persistedVariantId) continue;
      variants.set(persistedVariantId, {
        id: variant.id,
        persistedVariantId,
        itemNumber: upc.itemNumber,
        representativeCropId: variant.representativeCropId,
      });
    }
  }
  return variants;
}

function flattenDraftVariants(upcs: MasterUPCData[]): VariantSnapshot[] {
  const drafts: VariantSnapshot[] = [];
  for (const upc of upcs) {
    for (const variant of upc.variants) {
      if (variant.persistedVariantId) continue;
      drafts.push({
        id: variant.id,
        persistedVariantId: null,
        itemNumber: upc.itemNumber,
        representativeCropId: variant.representativeCropId,
        cropIds: sortIds(variant.crops.map((crop) => crop.id)),
      });
    }
  }
  return drafts;
}

function finalizeSummary(accByItem: Map<string, ItemDiffAccumulator>, totalChanges: number): MasterLabelDiffSummary {
  const itemSummaries = [...accByItem.values()]
    .map((acc) => ({
      itemNumber: acc.itemNumber,
      changeCount:
        acc.cropChangeCount +
        acc.representativeChangeCount +
        acc.draftVariantCount,
      cropChangeCount: acc.cropChangeCount,
      representativeChangeCount: acc.representativeChangeCount,
      draftVariantCount: acc.draftVariantCount,
      dirtyCropIds: sortIds([...acc.dirtyCropIds]),
      dirtyVariantIds: sortIds([...acc.dirtyVariantIds]),
    }))
    .filter((summary) => summary.changeCount > 0)
    .sort((a, b) => a.itemNumber.localeCompare(b.itemNumber));

  return {
    totalChanges,
    dirtyItemNumbers: itemSummaries.map((summary) => summary.itemNumber),
    itemSummaries,
  };
}

export function computeMasterLabelDiffSummary(
  baseUpcs: MasterUPCData[],
  workingUpcs: MasterUPCData[]
): MasterLabelDiffSummary {
  const accByItem = new Map<string, ItemDiffAccumulator>();
  const basePlacements = flattenCropPlacements(baseUpcs);
  const workingPlacements = flattenCropPlacements(workingUpcs);
  const cropIds = new Set([...basePlacements.keys(), ...workingPlacements.keys()]);

  let cropChangeTotal = 0;
  for (const cropId of cropIds) {
    const basePlacement = basePlacements.get(cropId);
    const workingPlacement = workingPlacements.get(cropId);
    if (cropPlacementKey(basePlacement) === cropPlacementKey(workingPlacement)) continue;
    cropChangeTotal += 1;
    for (const itemNumber of new Set([basePlacement?.itemNumber, workingPlacement?.itemNumber].filter(Boolean) as string[])) {
      const acc = ensureAccumulator(accByItem, itemNumber);
      acc.cropChangeCount += 1;
      acc.dirtyCropIds.add(cropId);
    }
  }

  const basePersistedVariants = flattenPersistedVariants(baseUpcs);
  const workingPersistedVariants = flattenPersistedVariants(workingUpcs);
  const persistedVariantIds = new Set([...basePersistedVariants.keys(), ...workingPersistedVariants.keys()]);

  let representativeChangeTotal = 0;
  for (const variantId of persistedVariantIds) {
    const baseVariant = basePersistedVariants.get(variantId);
    const workingVariant = workingPersistedVariants.get(variantId);

    if (!baseVariant || !workingVariant) continue;

    if (baseVariant.representativeCropId !== workingVariant.representativeCropId) {
      representativeChangeTotal += 1;
      for (const itemNumber of new Set([baseVariant.itemNumber, workingVariant.itemNumber])) {
        const acc = ensureAccumulator(accByItem, itemNumber);
        acc.representativeChangeCount += 1;
        acc.dirtyVariantIds.add(variantId);
      }
    }
  }

  const draftVariants = flattenDraftVariants(workingUpcs);
  let draftVariantTotal = 0;
  for (const draftVariant of draftVariants) {
    draftVariantTotal += 1;
    const acc = ensureAccumulator(accByItem, draftVariant.itemNumber);
    acc.draftVariantCount += 1;
    acc.dirtyVariantIds.add(draftVariant.id);
  }

  return finalizeSummary(
    accByItem,
    cropChangeTotal + representativeChangeTotal + draftVariantTotal
  );
}

function serializeVariantState(variant: MasterUPCData['variants'][number]): DesiredVariantState {
  return {
    clientVariantId: variant.id,
    _id: variant.persistedVariantId ?? null,
    barcodeStatus: variant.barcodeStatus,
    representativeCropId: variant.representativeCropId || null,
    cropIds: sortIds(variant.crops.map((crop) => crop.id)),
  };
}

function serializeDesiredItemState(upc: MasterUPCData): DesiredItemState {
  return {
    item_number: upc.itemNumber,
    snapshotVersion: upc.snapshotVersion || '',
    variants: [...upc.variants]
      .filter((variant) => (
        variant.crops.length > 0
        || (Boolean(variant.persistedVariantId) && Boolean(variant.verifiedBarcode))
      ))
      .sort((a, b) => a.id.localeCompare(b.id))
      .map(serializeVariantState),
    pendingCropIds: sortIds((upc.pendingCrops ?? []).map((crop) => crop.id)),
    unassignedCropIds: sortIds(upc.unassignedCrops.map((crop) => crop.id)),
    nonFrontCropIds: sortIds(upc.nonFrontCrops.map((crop) => crop.id)),
    cartonCropIds: sortIds(upc.cartonCrops.map((crop) => crop.id)),
    inactiveCropIds: sortIds((upc.inactiveCrops ?? []).map((crop) => crop.id)),
    differentItemCropIds: sortIds(upc.differentItemCropIds ?? []),
  };
}

export function buildMasterLabelStateSaveRequest(
  chainId: number,
  baseUpcs: MasterUPCData[],
  workingUpcs: MasterUPCData[]
): MasterLabelStateSaveRequest {
  const diffSummary = computeMasterLabelDiffSummary(baseUpcs, workingUpcs);
  const dirtyItemNumbers = new Set(diffSummary.dirtyItemNumbers);
  return {
    chain_id: chainId,
    items: workingUpcs
      .filter((upc) => dirtyItemNumbers.has(upc.itemNumber))
      .sort((a, b) => a.itemNumber.localeCompare(b.itemNumber))
      .map(serializeDesiredItemState),
  };
}

export function buildMasterLabelDebugInfo(
  chainId: number,
  baseUpcs: MasterUPCData[],
  workingUpcs: MasterUPCData[]
): MasterLabelDebugInfo {
  const summary = computeMasterLabelDiffSummary(baseUpcs, workingUpcs);
  const request = buildMasterLabelStateSaveRequest(chainId, baseUpcs, workingUpcs);
  const basePlacements = flattenCropPlacements(baseUpcs);
  const workingPlacements = flattenCropPlacements(workingUpcs);
  const cropIds = new Set([...basePlacements.keys(), ...workingPlacements.keys()]);
  const cropChanges = [...cropIds]
    .filter((cropId) => cropPlacementKey(basePlacements.get(cropId)) !== cropPlacementKey(workingPlacements.get(cropId)))
    .sort((left, right) => left.localeCompare(right))
    .map((cropId) => {
      const basePlacement = basePlacements.get(cropId);
      const workingPlacement = workingPlacements.get(cropId);
      return {
        cropId,
        baseItemNumber: basePlacement?.itemNumber ?? null,
        baseSection: basePlacement?.section ?? null,
        baseVariantId: basePlacement?.variantId ?? null,
        nextItemNumber: workingPlacement?.itemNumber ?? null,
        nextSection: workingPlacement?.section ?? null,
        nextVariantId: workingPlacement?.variantId ?? null,
      };
    });

  const basePersistedVariants = flattenPersistedVariants(baseUpcs);
  const workingPersistedVariants = flattenPersistedVariants(workingUpcs);
  const persistedVariantIds = new Set([...basePersistedVariants.keys(), ...workingPersistedVariants.keys()]);
  const representativeChanges = [...persistedVariantIds]
    .map((variantId) => {
      const baseVariant = basePersistedVariants.get(variantId);
      const workingVariant = workingPersistedVariants.get(variantId);
      if (!baseVariant || !workingVariant) return null;
      if (baseVariant.representativeCropId === workingVariant.representativeCropId) return null;
      return {
        variantId,
        baseItemNumber: baseVariant.itemNumber,
        nextItemNumber: workingVariant.itemNumber,
        baseRepresentativeCropId: baseVariant.representativeCropId,
        nextRepresentativeCropId: workingVariant.representativeCropId,
      };
    })
    .filter((change): change is NonNullable<typeof change> => Boolean(change))
    .sort((left, right) => left.variantId.localeCompare(right.variantId));

  const draftVariants = flattenDraftVariants(workingUpcs)
    .map((variant) => ({
      variantId: variant.id,
      itemNumber: variant.itemNumber,
      representativeCropId: variant.representativeCropId,
      cropIds: variant.cropIds ?? [],
    }))
    .sort((left, right) => left.variantId.localeCompare(right.variantId));

  return {
    summary,
    request,
    cropChanges,
    representativeChanges,
    draftVariants,
  };
}
